import { useEffect, useRef, useState } from 'react';

import { useDebouncedCallback } from '@klareospace/pharos-lib-front-utils';
import { USER_PREFERENCE_CATEGORY } from '@klareospace/pharos-lib-types';
import type { UserPreference } from '@klareospace/pharos-lib-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpsertUserPreference } from './use-upsert-user-preference';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Stable empty array returned when no order preference exists yet. */
const NO_COLUMN_ORDER: string[] = [];
const COLUMN_ORDER_SAVE_DEBOUNCE_MS = 500;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseColumnOrderPreferenceInput {
  /** User-preference key this column order is stored under. */
  key: string;
  /** Caller's own `useUserPreferences(...)` data. */
  preferences: UserPreference[] | undefined;
}

export interface UseColumnOrderPreferenceReturn {
  columnOrder: string[];
  onColumnOrderChange: (order: string[]) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Loads and persists a table's column-order preference: optimistic apply (hook state), debounced save, revert-to-server + toast on error. */
export function useColumnOrderPreference({
  key,
  preferences,
}: UseColumnOrderPreferenceInput): UseColumnOrderPreferenceReturn {
  const { t: tCommon } = useTranslation('common');

  const pref = preferences?.find((p) => p.key === key);
  const serverOrder = isStringArray(pref?.value) ? pref.value : NO_COLUMN_ORDER;

  const [optimisticOrder, setOptimisticOrder] = useState<string[] | null>(null);
  const columnOrder = optimisticOrder ?? serverOrder;

  // Once the server value catches up to the pending optimistic one, clear it — later external
  // changes (e.g. another tab) then show through instead of being masked by a stale optimistic order.
  useEffect(() => {
    if (optimisticOrder && serverOrder.join(' ') === optimisticOrder.join(' ')) {
      setOptimisticOrder(null);
    }
  }, [serverOrder, optimisticOrder]);

  const { mutate } = useUpsertUserPreference({
    onError: (): void => {
      toast.error(tCommon('toast.connectionError'));
      setOptimisticOrder(null);
    },
    silentSuccess: true,
  });

  const pendingOrderRef = useRef<string[] | null>(null);

  const debouncedSave = useDebouncedCallback((): void => {
    const pendingOrder = pendingOrderRef.current;
    if (!pendingOrder) return;
    mutate({ category: USER_PREFERENCE_CATEGORY.FRONTOFFICE, key, value: pendingOrder });
  }, COLUMN_ORDER_SAVE_DEBOUNCE_MS);

  function handleColumnOrderChange(order: string[]): void {
    setOptimisticOrder(order);
    pendingOrderRef.current = order;
    debouncedSave();
  }

  return { columnOrder, onColumnOrderChange: handleColumnOrderChange };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}
